import type { ReactNode } from 'react';

import type { Button } from '@root/sanity/sanity.types';
import type { Bundle, Item, Product } from 'types';
import IconArrowRight from 'components/IconArrowRight';
import Line from 'components/Line';
import Picture from 'components/Picture';
import SanityButton from 'components/sanity/Button';
import TrackedLink from 'components/sanity/TrackedLink';
import { getCollectionUrl } from 'utils/utils';

const getItemUrl = (item: Item) => {
  if (item.category !== 'preconfigured') {
    return `/collection/${getCollectionUrl(item.category!)}/${item.url_slug}`;
  }

  return `/collection/tuxedos-and-suits/${item.url_slug}`;
};

const getCatalogNumber = (product: Item) =>
  product.sku || product.catalogNumber || (product as Bundle).products!.find((x) => x.sku.startsWith('1'))?.sku;

const getProductImageUrl = (product: Item) => {
  const catalogNumber = getCatalogNumber(product);
  const imageName = `${catalogNumber}-jacket_full`;

  return process.env.NEXT_PUBLIC_LOOK_BUILDER_ASSETS_BASE_URL + '/' + imageName + '.png';
};

type GalleryProductRowProps = {
  as: string;
  className?: string;
  href: string;
  productAlt: string;
  productCost: string;
  productName: string;
  productUrl: string;
  tracker: string;
} & DataTestIdProp;

const GalleryProductRow = ({
  as,
  className,
  href,
  productAlt,
  productCost,
  productName,
  productUrl,
  tracker,
  ...props
}: GalleryProductRowProps) => (
  <TrackedLink
    href={href}
    as={as}
    data-testid={props['data-testid'] ?? 'gallery-product-row'}
    className={`group block ${tracker} ${className ? className : ''}`}
  >
    <div className="row">
      <div className="col-span-2">
        <img
          alt={productAlt}
          className="block aspect-ratio-1by1"
          height="40"
          loading="lazy"
          src={`${productUrl}?q=70&fit=crop&w=40&h=40&auto=format&dpr=2`}
          width="40"
        />
      </div>

      <div className="col-span-10">
        <div className="flex flex-wrap items-center space-x-8">
          <span className="text-gold-darker group-hover:underline">{productName}</span>

          <IconArrowRight className="!text-gold-darker" />
        </div>

        <p className="text-sm">${productCost}</p>
      </div>
    </div>
  </TrackedLink>
);

export type GalleryProductBlockProps = {
  bundle?: Bundle;
  className?: string;
  content: string | ReactNode;
  ctaButton?: Button;
  horizontalImg: {
    url: string;
    alt: string;
  };
  polaroidImg: {
    url: string;
    alt: string;
  };
  products?: Product[];
  reversed: boolean;
  title: string;
  trackerPageName: string;
} & DataTestIdProp;

/**
 * GalleryProductBlock - Sanity Component
 */
const GalleryProductBlock = ({
  bundle,
  className,
  content,
  ctaButton,
  horizontalImg,
  polaroidImg,
  products,
  title,
  trackerPageName,
  reversed = false,
  ...props
}: GalleryProductBlockProps) => {
  const ContentJsx = (
    <div className="space-y-32">
      {bundle && (
        <TrackedLink
          href="/collection/[category]/[slug]"
          as={getItemUrl(bundle)}
          className={`tracker-link-${trackerPageName}-color-section_${title
            .replace(/ /g, '')
            .toLowerCase()}-header_${bundle.display_name!.replace(/ /g, '').toLowerCase()}-230131-134723`}
          data-testid="gpb-title-link"
        >
          <h2 className="text-h4 !text-gold-darker">{title}</h2>
        </TrackedLink>
      )}

      {content}

      <Line />

      <div className="space-y-8 md:space-y-16 lg:space-y-32">
        {bundle && (
          <GalleryProductRow
            as={getItemUrl(bundle)}
            data-testid="gpb-bundle"
            href="/collection/[category]/[slug]"
            key={bundle.display_name!}
            tracker={`tracker-link-${trackerPageName}-color-section_${title
              .replace(/ /g, '')
              .toLowerCase()}-product_${bundle.display_name!.replace(/ /g, '').toLowerCase()}-230131-134725`}
            productUrl={getProductImageUrl(bundle)}
            productAlt={bundle.display_name!}
            productName={bundle.display_name!}
            productCost={bundle.cost!}
          />
        )}

        {products &&
          products!.map((product, index) => (
            <GalleryProductRow
              as={getItemUrl(product)}
              data-testid="gpb-product"
              href="/collection/[category]/[slug]"
              key={`${product.display_name!}_${index}`}
              tracker={`tracker-link-${trackerPageName}-color-section_${title
                .replace(/ /g, '')
                .toLowerCase()}-product_${product.display_name!.replace(/ /g, '').toLowerCase()}-230131-134726`}
              productUrl={`${product.media!.find((m) => m.label === 'PDP_Hero')?.url}`}
              productAlt={`${product.display_name}`}
              productName={`${product.display_name}`}
              productCost={`${product.cost}`}
            />
          ))}
      </div>

      {ctaButton && <SanityButton {...ctaButton} />}
    </div>
  );

  const HorizontalImgJsx = (
    <Picture
      alt={horizontalImg.alt}
      className="aspect-ratio-7by5"
      data-testid="gpb-horizontal-img"
      srcSet={{
        lg: 376,
        md: 279,
        sm: 263,
        xs: 254,
        base: 266,
      }}
      url={horizontalImg.url}
    />
  );

  const PolaroidImgJsx = (
    <Picture
      alt={polaroidImg.alt}
      className="aspect-ratio-5by7"
      data-testid="gpb-polaroid-img"
      polaroid
      srcSet={{ lg: 242, md: 170, sm: 172, xs: 132, base: 201 }}
      url={polaroidImg.url}
    />
  );

  return (
    <div data-testid={props['data-testid'] ?? 'gallery-product-block'} className="m-section">
      <div className={`container ${className ? className : ''}`}>
        {reversed ? (
          <div className="row">
            <div className="col-span-12 md:col-span-6">
              <div className="row -mb-64 sm:-mb-128">
                <div className="col-span-3 md:col-span-4" />
                <div className="sm:-col-span-5 col-span-7 xs:col-span-6 md:col-span-8">{HorizontalImgJsx}</div>
              </div>
              <div className="row mb-32 xs:-mb-128">
                <div className="col-span-5 xs:col-span-4 md:col-span-6">{PolaroidImgJsx}</div>
              </div>
            </div>
            <div className="col-span-10 xs:col-span-7 xs:col-start-6 sm:col-span-6 sm:col-start-6 md:col-span-5 md:col-start-auto lg:col-span-4">
              {ContentJsx}
            </div>
          </div>
        ) : (
          <div className="row">
            <div
              className="
              col-span-10 
              xs:col-span-7
              sm:col-span-6
              md:col-span-5 md:col-start-2
              lg:col-span-4 lg:col-start-3
            "
            >
              {ContentJsx}
            </div>
            <div
              className="
              col-span-12 row-start-1 
              md:col-span-6 md:row-start-auto
            "
            >
              <div className="row -mb-64 sm:-mb-128">
                <div className="col-span-2 xs:col-span-3 sm:col-span-4 md:hidden" />
                <div className="sm:-col-span-5 col-span-7 xs:col-span-6 md:col-span-8 ">{HorizontalImgJsx}</div>
              </div>
              <div className="row mb-32 xs:-mb-128">
                <div className="col-span-7 xs:col-span-8 md:col-span-6" />
                <div className="col-span-5 xs:col-span-4 md:col-span-6">{PolaroidImgJsx}</div>
                <div className="col-span-2 xs:col-span-3 sm:col-span-4 md:hidden" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryProductBlock;
